import React, { useState, useMemo, useEffect } from "react"
import {
  Table,
  Tag,
  Button,
  Progress,
  Avatar,
  Space,
  Dropdown,
  Spin,
} from "antd"
import { CopyOutlined, DownloadOutlined, DownOutlined } from "@ant-design/icons"
import { translations } from "../../utils/Translation/staticTranslation"
import { message } from "antd"
import { Discounts as DiscountsApi } from "../../Apis/discountsApi"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import "./Discounts.css"

dayjs.extend(utc)

const getCountryName = (code, lang = "en") => {
  if (!code) return lang === "ar" ? "غير معروف" : "Unknown"
  try {
    const formatter = new Intl.DisplayNames([lang === "ar" ? "ar" : "en"], {
      type: "region",
    })
    return formatter.of(code.toUpperCase()) || code.toUpperCase()
  } catch {
    return code.toUpperCase()
  }
}

const getTypeValueDisplay = (type, value) => {
  const normalizedType =
    type?.toString().toLowerCase().replace(/\s+/g, "_") || ""
  if (normalizedType === "percentage") {
    return `${value}% off`
  } else if (normalizedType === "fixed") {
    return `SAR ${value} off`
  } else if (normalizedType === "trial_extension") {
    return `+${value} days`
  } else if (normalizedType === "plan_extension") {
    const monthsLabel = Number(value) === 1 ? "month" : "months"
    return `${value} ${monthsLabel}`
  }
  return `${value} ${type}`
}

const formatDate = (dateString) => {
  if (!dateString) return ""
  // Backend now sends times with timezone offsets (e.g., +05:00)
  // Parse normally - dayjs will handle the timezone offset correctly
  const date = dayjs(dateString)
  return date.format("YYYY-MM-DD HH:mm")
}

const DiscountDetailView = ({ discount, onBack, loading = false }) => {
  const lang = localStorage.getItem("language") || "ar"
  const t = translations[lang]?.discountsPage
  const isRTL = lang === "ar"

  const [usersData, setUsersData] = useState([])
  const [usersLoading, setUsersLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [userPagination, setUserPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  // Fetch discount users when discount is loaded
  useEffect(() => {
    const fetchDiscountUsers = async () => {
      if (!discount?.id) return

      try {
        setUsersLoading(true)
        const response = await DiscountsApi.getDiscountUsers(
          discount.id,
          userPagination.current,
          userPagination.pageSize
        )

        if (response?.data) {
          // Handle both array and object responses
          let results = response.data.results
          if (!Array.isArray(results)) {
            // If results is an object, wrap it in an array
            results = results && typeof results === "object" ? [results] : []
          }

          // Map API response to component format
          const mappedUsers = results.map((item, index) => {
            const countryCode = item.country || ""
            const countryName = getCountryName(countryCode, lang)

            return {
              id: item.id || index + 1,
              name: item.user_name || "",
              email: item.email || "",
              profile: item.profile || null,
              date: item.date || "",
              plan: item.plan || "",
              country: countryName,
              countryCode: countryCode,
              originalAmount: parseFloat(item.original_amount || 0),
              paidAmount: parseFloat(item.paid_amount || 0),
            }
          })

          setUsersData(mappedUsers)
          setUserPagination((prev) => ({
            ...prev,
            total: response.data.count || 0,
          }))
        }
      } catch (error) {
        console.error("Error fetching discount users:", error)
        message.error(
          t?.error_fetching_users || "Error fetching discount users"
        )
      } finally {
        setUsersLoading(false)
      }
    }

    fetchDiscountUsers()
  }, [discount?.id, userPagination.current, userPagination.pageSize, lang, t])

  const getStatusTag = (status) => {
    const normalizedStatus = (status || "").toLowerCase()
    const statusMap = {
      active: "Active",
      expired: "Expired",
      upcoming: "Upcoming",
    }
    const displayStatus = statusMap[normalizedStatus] || status || "Active"

    // Get Arabic translations if language is Arabic
    let statusText = displayStatus
    if (isRTL) {
      const statusTranslations = {
        Active: t?.filters?.status?.active || "نشط",
        Expired: t?.filters?.status?.expired || "منتهيالصلاحية",
        Upcoming: t?.filters?.status?.upcoming || "قادم",
      }
      statusText = statusTranslations[displayStatus] || displayStatus
    }

    const statusConfig = {
      Active: { color: "#027A48", bg: "#ECFDF3", border: "#ABEFC6" },
      Expired: { color: "#667085", bg: "#F2F4F7", border: "#E4E7EC" },
      Upcoming: { color: "#155EEF", bg: "#EFF8FF", border: "#B2DDFF" },
    }
    const config = statusConfig[displayStatus] || statusConfig.Active

    return (
      <Tag
        style={{
          color: config.color,
          backgroundColor: config.bg,
          border: `1px solid ${config.border}`,
          borderRadius: "6px",
          padding: "2px 8px",
          fontSize: "12px",
          fontWeight: 500,
        }}
      >
        {statusText}
      </Tag>
    )
  }

  const handleCopyCode = () => {
    if (discount?.discountCode) {
      navigator.clipboard.writeText(discount.discountCode)
      message.success(t?.code_copied || "Code copied to clipboard")
    }
  }

  const handleExport = async () => {
    if (!discount?.id) {
      message.error(t?.discount_id_missing || "Discount ID is missing")
      return
    }

    try {
      setExportLoading(true)
      const response = await DiscountsApi.exportDiscountUsers(discount.id)

      // Create blob from response
      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      })

      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `discount-users-${discount.discountCode || discount.id}-${
        new Date().toISOString().split("T")[0]
      }.csv`
      document.body.appendChild(link)
      link.click()

      // Clean up
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      message.success(t?.export_success || "Export successful")
    } catch (error) {
      console.error("Export error:", error)
      message.error(
        error.response?.data?.error ||
          t?.export_error ||
          "Failed to export discount users"
      )
    } finally {
      setExportLoading(false)
    }
  }

  // Users are already paginated from API, so use them directly
  const paginatedUsers = useMemo(() => {
    return usersData
  }, [usersData])

  const columns = [
    {
      title: t?.detail?.user || "User",
      dataIndex: "name",
      key: "user",
      render: (text, record) => (
        <Space>
          <Avatar
            src={
              record.profile ||
              `https://ui-avatars.com/api/?name=${encodeURIComponent(
                text
              )}&background=random`
            }
          />
          <div>
            <div style={{ fontWeight: 500, color: "#101828" }}>{text}</div>
            <div style={{ color: "#8C8C8C", fontSize: "12px" }}>
              {record.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: t?.detail?.date || "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => <span style={{ color: "#8C8C8C" }}>{date}</span>,
    },
    {
      title: t?.detail?.plan || "Plan",
      dataIndex: "plan",
      key: "plan",
      render: (plan) => {
        if (!plan) return null
        // Handle both string (comma-separated) and array formats
        const plans = Array.isArray(plan) ? plan : plan.split(", ")
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
            {plans.map((p, index) => (
              <Tag
                key={index}
                style={{
                  color: "#344054",
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #E4E7EC",
                  borderRadius: "6px",
                  padding: "2px 8px",
                  fontSize: "12px",
                  fontWeight: 500,
                }}
              >
                {p}
              </Tag>
            ))}
          </div>
        )
      },
    },
    {
      title: t?.detail?.country || "Country",
      dataIndex: "country",
      key: "country",
      render: (text, record) => (
        <Space>
          {record.countryCode && record.countryCode !== "EU" ? (
            <img
              src={`https://flagcdn.com/w40/${record.countryCode.toLowerCase()}.png`}
              alt={record.countryCode}
              style={{
                width: "20px",
                height: "20px",
                borderRadius: "4px",
                objectFit: "cover",
              }}
            />
          ) : (
            <span style={{ fontSize: "14px" }}>🌍</span>
          )}
          <span style={{ color: "#101828" }}>{text}</span>
        </Space>
      ),
    },
    {
      title: t?.detail?.original_amount || "Original Amount",
      dataIndex: "originalAmount",
      key: "originalAmount",
      render: (amount) => (
        <span style={{ color: "#101828", fontWeight: 500 }}>SAR {amount}</span>
      ),
    },
    {
      title: t?.detail?.paid_amount || "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      render: (amount) => (
        <span style={{ color: "#101828", fontWeight: 500 }}>SAR {amount}</span>
      ),
    },
  ]

  if (loading) {
    return (
      <div className="discount-detail-container">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    )
  }

  if (!discount) return null

  const used = discount.used || 0
  const total = discount.total
  const hasMaxRedemptions = total !== null && total !== undefined
  const percentage =
    hasMaxRedemptions && total > 0 ? Math.round((used / total) * 100) : 0

  // Get audience and audiance tag - use Arabic if available and language is Arabic
  const audienceText =
    isRTL && discount.audience_arabic
      ? discount.audience_arabic
      : discount.audience || ""
  const audianceTag =
    isRTL && discount.audianceTag_arabic
      ? discount.audianceTag_arabic
      : discount.audianceTag || ""

  return (
    <div className="discount-detail-container">
      {/* Breadcrumbs */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "24px",
          color: "#667085",
          fontSize: "14px",
        }}
      >
        <span style={{ cursor: "pointer" }} onClick={onBack}>
          {t?.breadcrumbs?.discounts || "Discounts"}
        </span>
        <img
          src="/discounts/angle-right.svg"
          alt=""
          style={{ width: "16px", height: "16px" }}
        />
        <span style={{ color: "#101828", fontWeight: 500 }}>
          {discount.discountCode}
        </span>
      </div>

      {/* Discount Overview Section */}
      <div className="discount-detail-card">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 0,
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <h1
                style={{
                  fontSize: "24px",
                  fontWeight: 600,
                  color: "#101828",
                  margin: 0,
                }}
              >
                {discount.discountCode}
              </h1>
              {getStatusTag(discount.status)}
            </div>
            <p style={{ color: "#4A5565", fontSize: "12px", margin: 0 }}>
              {isRTL && discount.description_arabic
                ? discount.description_arabic
                : discount.description || ""}
            </p>
          </div>
          <Button
            onClick={handleCopyCode}
            className="discount-copy-button"
            style={{
              height: "40px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              backgroundColor: "#F3F3F3",
              border: "none",
              color: "#101828",
            }}
          >
            <img
              src="/discounts/copy-icon.svg"
              alt="copy"
              style={{ width: "16px", height: "16px" }}
            />
            {t?.copy_code || "Copy Code"}
          </Button>
        </div>

        {/* Discount Attributes */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "140px 1fr 1fr 1fr 140px",
            gap: "32px",
            paddingTop: "24px",
          }}
        >
          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.discount_type || "Discount Type"}
            </div>
            <div
              style={{
                color: "#101828",
                fontSize: "15px",
                fontWeight: 500,
              }}
            >
              {getTypeValueDisplay(discount.type, discount.value)}
            </div>
          </div>

          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.audience || "Audience"}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: "#101828",
                  fontWeight: "500",
                  fontSize: "14px",
                }}
              >
                {audienceText}
              </span>
              {audianceTag && (
                <Tag
                  style={{
                    color: "#344054",
                    backgroundColor: "#F9FAFB",
                    border: "1px solid #E4E7EC",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {audianceTag}
                </Tag>
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.validity_period || "Validity Period"}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "#101828", fontSize: "14px" }}>
                {formatDate(discount.startDate)}
              </span>
              <span style={{ color: "#8C8C8C", fontSize: "14px" }}>→</span>
              <span style={{ color: "#8C8C8C", fontSize: "14px" }}>
                {formatDate(discount.endDate)}
              </span>
            </div>
          </div>

          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.remaining_usage || "Remaining Usage"}
            </div>
            <div style={{ marginBottom: "0px" }}>
              <span
                style={{ color: "#101828", fontSize: "14px", fontWeight: 500 }}
              >
                {hasMaxRedemptions ? `${used} / ${total}` : used}
              </span>
            </div>
            {hasMaxRedemptions && (
              <div style={{ width: "115px", marginTop: "-4px" }}>
                <Progress
                  percent={percentage}
                  showInfo={false}
                  strokeColor="#007AFF"
                  trailColor="#E5E7EB"
                  size="small"
                />
              </div>
            )}
          </div>

          <div>
            <div
              style={{
                color: "#8C8C8C",
                fontSize: "14px",
              }}
            >
              {t?.detail?.plans || "Plans"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {(isRTL && discount.targetPlans_arabic
                ? discount.targetPlans_arabic
                : discount.targetPlans || []
              ).map((plan, index) => (
                <Tag
                  key={index}
                  style={{
                    color: "#344054",
                    backgroundColor: "#F9FAFB",
                    border: "1px solid #E4E7EC",
                    borderRadius: "6px",
                    padding: "2px 8px",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {plan}
                </Tag>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Discount Users Table */}
      <div
        className="discount-detail-card discount-users-card"
        style={{ marginBottom: 0 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
            paddingLeft: "16px",
            paddingRight: "16px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              color: "#101828",
              margin: 0,
            }}
          >
            {t?.detail?.discount_users || "Discount Users"}
          </h2>
          <Dropdown
            menu={{
              items: [
                {
                  key: "csv",
                  label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "14px",
                        color: "#101828",
                      }}
                    >
                      <DownloadOutlined style={{ fontSize: 16 }} />
                      {t?.export_csv || "CSV File"}
                    </div>
                  ),
                },
              ],
              onClick: handleExport,
            }}
            trigger={["click"]}
          >
            <Button
              type="primary"
              loading={exportLoading}
              style={{
                backgroundColor: "#3E3A36",
                color: "#fff",
                borderRadius: "8px",
                height: "36px",
                fontWeight: 500,
                padding: "0 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
              }}
            >
              {t?.export || "Export"}
              <DownOutlined style={{ fontSize: 12 }} />
            </Button>
          </Dropdown>
        </div>

        <div className="discount-detail-table-container">
          <Table
            columns={columns}
            dataSource={paginatedUsers}
            loading={usersLoading}
            pagination={{
              current: userPagination.current,
              total: userPagination.total,
              pageSize: userPagination.pageSize,
              showSizeChanger: false,
              onChange: (page) => {
                setUserPagination((prev) => ({ ...prev, current: page }))
              },
            }}
            rowKey="id"
          />
        </div>
      </div>
    </div>
  )
}

export default DiscountDetailView
